import * as React from "react";
import { useContext, useRef, useCallback, useMemo } from "react";
import i18n from "i18n";
import UserNotification from "util/UserNotification";
import DisableSubmissionStateContext from "views/components/contexts/DisableSubmissionStateContext";
import WidgetEditApplyAllChangesContext from "./WidgetEditApplyAllChangesContext";
const useBindApplyChanges = () => {
  const applyChangesRef = useRef(null);
  const bindApplyChanges = useCallback((updateWidgetConfig) => {
    applyChangesRef.current = updateWidgetConfig;
  }, []);
  return { applyChangesRef, bindApplyChanges };
};
const useApplyAllWidgetChanges = (widget, applySearchControlsChanges, applyElementConfigurationChanges, onSubmit) => {
  const { setDisabled } = useContext(DisableSubmissionStateContext);
  const setDisableWidgetEditSubmit = useCallback(
    (disabled) => setDisabled("widget-edit-apply-all-changes", disabled),
    [setDisabled]
  );
  return useCallback(() => {
    let newWidget = widget;
    let hasChanges = false;
    if (applySearchControlsChanges.current) {
      const updatedWidget = applySearchControlsChanges.current(newWidget);
      if (updatedWidget) {
        newWidget = updatedWidget;
        hasChanges = hasChanges || true;
      }
    }
    if (applyElementConfigurationChanges.current) {
      const updatedWidgetConfig = applyElementConfigurationChanges.current(newWidget.config);
      if (updatedWidgetConfig) {
        newWidget = newWidget.toBuilder().config(updatedWidgetConfig).build();
        hasChanges = hasChanges || true;
      }
    }
    setDisableWidgetEditSubmit(true);
    return onSubmit(newWidget, hasChanges).catch((error) => {
      UserNotification.error(i18n.t("views_notifications.apply_widget_changes_failed", { ns: "search", error }));
      return error;
    }).finally(() => setDisableWidgetEditSubmit(false));
  }, [widget, applySearchControlsChanges, applyElementConfigurationChanges, setDisableWidgetEditSubmit, onSubmit]);
};
const WidgetEditApplyAllChangesProvider = ({ children, widget, onSubmit }) => {
  const { applyChangesRef: applySearchControlsChanges, bindApplyChanges: bindApplySearchControlsChanges } = useBindApplyChanges();
  const { applyChangesRef: applyElementConfigurationChanges, bindApplyChanges: bindApplyElementConfigurationChanges } = useBindApplyChanges();
  const applyAllWidgetChanges = useApplyAllWidgetChanges(
    widget,
    applySearchControlsChanges,
    applyElementConfigurationChanges,
    onSubmit
  );
  const contextValue = useMemo(
    () => ({
      applyAllWidgetChanges,
      bindApplyElementConfigurationChanges,
      bindApplySearchControlsChanges
    }),
    [applyAllWidgetChanges, bindApplyElementConfigurationChanges, bindApplySearchControlsChanges]
  );
  return /* @__PURE__ */ React.createElement(WidgetEditApplyAllChangesContext.Provider, { value: contextValue }, children);
};
export default WidgetEditApplyAllChangesProvider;
